import { z } from "zod";
/**
 * Per-user insight events. Persisted in the same transaction as the state
 * change that causes them (transactional outbox semantics). The web polls
 * GET /v1/insights; there is no push channel or message bus.
 *
 * Producers: RESEARCH_FINISHED (calculator research for a calculation or
 * upload) and RESULT_CHANGED (automatic recalculation — later PR). The writer
 * is generic so both share one store and one API.
 */
export const insightKindSchema = z.enum(["RESEARCH_FINISHED", "RESULT_CHANGED"]);
export const insightReasonSchema = z.enum([
    /** A newly published rule now answers lines that were missing. */
    "NEW_RULE",
    /** A rule the result used changed or was removed in a published ruleset. */
    "RULE_CHANGED",
    /** Research found publication-grade data (published rule / certified / verified). */
    "DATA_FOUND",
    /** Research found only UNVERIFIED data: amounts unchanged until review. */
    "DATA_FOUND_UNVERIFIED",
    /** Research finished without an answer. */
    "DATA_NOT_FOUND",
    /** Research failed or was cancelled. */
    "RESEARCH_FAILED",
]);
export const insightSubjectKindSchema = z.enum(["CALCULATION", "UPLOAD", "LOT_TASK"]);
export const insightCountsSchema = z
    .object({
    requests: z.number().int().nonnegative(),
    found: z.number().int().nonnegative(),
    needsReview: z.number().int().nonnegative(),
    notFound: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    linesChanged: z.number().int().nonnegative(),
    linesTotal: z.number().int().nonnegative(),
})
    .strict();
/** After minus before of the total tax, BRL decimal string; null when not a recalculation or unknown. */
export const insightTaxDeltaSchema = z
    .string()
    .regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,2})?$/)
    .nullable();
export const insightSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    kind: insightKindSchema,
    subject: z
        .object({
        kind: insightSubjectKindSchema,
        id: z.string().uuid(),
        /** RESULT_CHANGED: the calculation / task the new one replaces. */
        previousId: z.string().uuid().nullable(),
    })
        .strict(),
    reasonCodes: z.array(insightReasonSchema).min(1).max(6),
    counts: insightCountsSchema,
    taxDelta: insightTaxDeltaSchema,
    rulesetVersion: z.string().min(1).max(80).nullable(),
    correlationId: z.string().min(1).max(200),
    createdAt: z.string().datetime(),
    readAt: z.string().datetime().nullable(),
})
    .strict();
export const insightPageSchema = z
    .object({
    items: z.array(insightSchema).max(100),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    unreadCount: z.number().int().nonnegative(),
})
    .strict();
export const insightMarkReadSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ids: z.array(z.string().uuid()).min(1).max(100).optional(),
    all: z.literal(true).optional(),
})
    .strict()
    .refine((v) => Boolean(v.ids) !== Boolean(v.all), { message: "Send ids or all, not both" });
export const insightMarkReadResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    updated: z.number().int().nonnegative(),
    unreadCount: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=insights.js.map